import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import DataTable from '../components/DataTable';

interface AuditEntry {
  id: string;
  admin_email: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  timestamp: string | null;
  ip_address: string;
  user_agent: string;
}

interface AuditResponse {
  entries: AuditEntry[];
  truncated: boolean;
  limit: number;
}

const ACTIONS = [
  '',
  'login_success',
  'login_denied',
  'list_tenants',
  'view_tenant',
  'list_users',
  'view_user',
  'view_app_data',
  'create_tenant',
  'create_user',
  'update_tenant',
  'update_user',
  'list_admins',
  'add_admin',
  'deactivate_admin',
  'view_audit_log',
];

function format_dt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

export default function Audit() {
  const [filter_email, set_filter_email] = useState('');
  const [filter_action, set_filter_action] = useState('');
  const [since, set_since] = useState('');
  const [until, set_until] = useState('');

  const qs = new URLSearchParams();
  if (filter_email.trim()) qs.set('admin_email', filter_email.trim());
  if (filter_action) qs.set('action', filter_action);
  if (since) qs.set('since', new Date(since).toISOString());
  if (until) qs.set('until', new Date(until).toISOString());
  const path = `/api/audit${qs.toString() ? `?${qs}` : ''}`;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['audit', filter_email, filter_action, since, until],
    queryFn: () => api.get<AuditResponse>(path),
  });

  return (
    <div className="container">
      <h1>Audit log</h1>
      <p className="muted" style={{ marginTop: -8, marginBottom: 24 }}>
        Most recent actions, newest first. Auto-deletes after 180 days.
      </p>

      <div className="card" style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 12,
            alignItems: 'end',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Admin email</span>
            <input
              value={filter_email}
              onChange={(e) => set_filter_email(e.target.value)}
              placeholder="filter by admin"
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Action</span>
            <select value={filter_action} onChange={(e) => set_filter_action(e.target.value)}>
              {ACTIONS.map((a) => (
                <option key={a} value={a}>
                  {a || '(all)'}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Since</span>
            <input
              type="datetime-local"
              value={since}
              onChange={(e) => set_since(e.target.value)}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Until</span>
            <input
              type="datetime-local"
              value={until}
              onChange={(e) => set_until(e.target.value)}
            />
          </label>
          <button className="secondary" onClick={() => refetch()} disabled={isFetching}>
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {isLoading && <p style={{ padding: 24, margin: 0 }}>Loading...</p>}
        {error && (
          <p className="error" style={{ margin: 24 }}>
            Failed to load: {error instanceof ApiError ? error.reason : String(error)}
          </p>
        )}
        {data && (
          <>
            <DataTable
              rows={data.entries}
              columns={[
                { header: 'Time', cell: (e) => format_dt(e.timestamp), width: '170px' },
                { header: 'Admin', cell: (e) => e.admin_email },
                { header: 'Action', cell: (e) => <code style={{ fontSize: 12 }}>{e.action}</code> },
                {
                  header: 'Target',
                  cell: (e) =>
                    e.target_id ? (
                      <span>
                        <span className="muted" style={{ fontSize: 12 }}>{e.target_type ?? ''}: </span>
                        <code style={{ fontSize: 12 }}>{e.target_id}</code>
                      </span>
                    ) : (
                      <span className="muted">—</span>
                    ),
                },
                { header: 'IP', cell: (e) => <code style={{ fontSize: 12 }}>{e.ip_address}</code> },
              ]}
              empty_message="No matching audit entries."
            />
            {data.truncated && (
              <p className="muted" style={{ padding: '12px 16px', fontSize: 13, margin: 0, borderTop: '1px solid var(--line)' }}>
                Showing the most recent {data.limit} entries (truncated). Narrow the filters to see older entries.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
